<template>
  <div class="flex flex-col justify-start" v-loading="loading">
    <div v-if="showBreadcrumb" class="flex flex-row items-center space-x-1">
      <div class="text-lg text-gray-400">
        <button @click="jumpToLiveProject">作品展示</button>
      </div>

      <div class="text-lg font-bold">/</div>
      <div class="text-lg font-bold">{{ workName }}</div>
    </div>

    <!-- 播放内容 -->
    <div v-if="layout === 'modal'" class="overflow-hidden mt-4 bg-white rounded-xl shadow-sm">
      <div class="relative w-full bg-black">
        <slot name="videoOverlay" />
        <video id="video-container" height="270" preload="auto" playsinline webkit-playsinline></video>
      </div>

      <div class="p-4 space-y-3 sm:p-5">
        <div class="flex gap-3 justify-between items-start">
          <div class="min-w-0">
            <div class="flex flex-wrap gap-2 items-center">
              <span class="text-base font-semibold text-gray-900 sm:text-lg line-clamp-2">
                {{ workName }}
              </span>
              <el-tag :type="contestCheck(workType)" size="small" effect="light">
                {{ checkContestName(workType) }}
              </el-tag>
            </div>
            <div class="mt-1 text-xs text-gray-500">
              <span v-if="workListDetail.work_author">作者：{{ workListDetail.work_author }}</span>
              <span v-if="workListDetail.work_author && workListDetail.work_created_time" class="mx-2">·</span>
              <span v-if="workListDetail.work_created_time">发布于：{{ formateDate(workListDetail.work_created_time) }}</span>
            </div>
          </div>
        </div>

        <div class="pt-2 border-t border-gray-100">
          <div class="text-sm font-medium text-gray-700">作品介绍</div>
          <div class="mt-2 text-sm leading-relaxed text-gray-600 break-words max-h-[38vh] overflow-auto">
            {{ workIntroduce }}
          </div>
        </div>
      </div>
    </div>

    <div v-else class="flex flex-col mt-5 space-x-5 bg-white rounded-lg shadow-sm">
      <div class="grid grid-cols-1 gap-6 p-4 lg:grid-cols-2 lg:gap-20 lg:p-10">
        <div class="flex flex-col space-y-5 h-full">
          <div class="flex flex-row flex-wrap gap-2 items-center">
            <span class="text-xl font-black"> {{ workListDetail.work_name }}</span>
            <el-tag :type="contestCheck(workType)" size="small" effect="light">{{ checkContestName(workType) }}</el-tag>
          </div>
          <div class="flex flex-col space-y-2">
            <span class="font-bold text-gray-400">作者： <br /></span>
            <span class="font-bold">{{ workListDetail.work_author }}</span>
          </div>
          <div class="flex flex-col space-y-2">
            <span class="font-bold text-gray-400">作品介绍:</span>
            <span class="flex w-full text-sm font-semibold leading-relaxed text-slate-600">
              {{ workListDetail.work_introduce }}
            </span>
          </div>
          <div class="flex flex-row text-xs text-gray-500">
            <span>发布于： </span>
            <span> {{ formateDate(workListDetail.work_created_time) }}</span>
          </div>
        </div>
        <div class="flex w-full rounded-lg">
          <video id="video-container" height="270" preload="auto" playsinline webkit-playsinline></video>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { getWorkDetail } from '~/composables/workList/getWorkDetail'
import { checkContestName, contestCheck } from '~/composables/workList/workListCard'

useHead({
  link: [
    {
      rel: 'stylesheet',
      href: 'https://web.sdk.qcloud.com/player/tcplayer/release/v5.1.0/tcplayer.min.css',
    },
  ],
  script: [
    {
      src: 'https://web.sdk.qcloud.com/player/tcplayer/release/v5.1.0/tcplayer.v5.1.0.min.js',
      async: true,
      defer: true,
      type: 'text/javascript',
    },
  ],
})

const props = withDefaults(
  defineProps<{
    workId?: number
    showBreadcrumb?: boolean
    layout?: 'default' | 'modal'
  }>(),
  {
    showBreadcrumb: true,
    layout: 'default',
  },
)

const route = useRoute()
const player = ref<any>(null)
const loading = ref(false)

const effectiveWorkId = computed(() => {
  if (typeof props.workId === 'number' && props.workId > 0) {
    return props.workId
  }
  const id = Number(route.query.id)
  return Number.isFinite(id) && id > 0 ? id : undefined
})

const workListDetail = ref<any>({})
const workName = computed(() => {
  const detail = workListDetail.value
  const name = detail && detail.work_name
  return name ? name : '作品名称'
})

const workIntroduce = computed(() => {
  const detail = workListDetail.value
  const introduce = detail && detail.work_introduce
  return introduce ? introduce : '暂无介绍'
})

const workType = computed(() => {
  const detail = workListDetail.value
  const type = detail && detail.work_type
  return typeof type === 'number' ? type : 0
})

const loadWorkDetail = async (id: number) => {
  loading.value = true
  try {
    const detail = await getWorkDetail(id)
    workListDetail.value = detail
    await nextTick()
    const videoFileId = workListDetail.value && workListDetail.value.work_video
    if (videoFileId) {
      playSin(player, videoFileId, 0)
    }
  } catch (e) {
    ElMessage({
      message: '网络请求错误，请稍后再试',
      type: 'error',
    })
  } finally {
    loading.value = false
  }
}

watch(
  effectiveWorkId,
  (id: number | undefined) => {
    if (!id) {
      return
    }
    loadWorkDetail(id)
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  const instance = player.value
  if (instance && typeof instance.dispose === 'function') {
    instance.dispose()
  }
})

//跳转到作品展示
function jumpToLiveProject() {
  const router = useRouter()
  router.back()
}
</script>

<style>
#video-container {
  width: 100%;
  max-width: 100%;
  height: 0;
  padding-top: 56.25%;
}

#wrap {
  width: 80%;
  margin: 0 auto;
}

@media only screen and (min-device-pixel-ratio: 2), only screen and (-webkit-min-device-pixel-ratio: 2) {
  .tcp-logo-img {
    width: 50%;
  }
}

@media screen and (max-width: 480px) {
  #video-container {
    width: 100%;
    height: 240px;
  }
}
</style>
